import React, { useEffect, useRef, useState } from 'react';

import styled from 'styled-components';
import { QRCodeCanvas } from 'qrcode.react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import type { FileError } from 'react-dropzone';

import { getExtendedFormattedDatetime } from 'helpers/dateTimes';

import { BarcodeType, GetActiveCategoriesQuery, GetDetailedProductQuery } from 'data/__generated__';

import { DefaultProductType } from 'pages/Products/constants/defaultProduct';
import { InfoTags } from 'pages/Products/components/InfoTags';

import { SectionColumn, SectionContainer, ColumnsSectionContainer } from 'components/DetailsView/Section';
import { DetailFormValue } from 'components/DetailsView/DetailFormValue';
import { DetailDisplayValue } from 'components/DetailsView/DetailDisplayValue';
import {
    DetailFormSelect,
    DetailFormTextArea,
    DetailValue,
    DetailFormImage,
    DetailList,
    DetailListRemoveButton,
} from 'components/DetailsView';
import { Rating } from 'components/Rating';
import { FileWithPreview } from 'components/FileDragAndDrop';
import { StatusTag } from '../components/StatusTag';
import { CopyValue } from 'components/CopyValue';
import { TotemImage } from 'components/TotemImage';

import { getProductFullName } from '../utils';
import { SubcategorySelector } from './components/SubcategorySelector';
import { QRCodeModal } from '../../../components/QRCodeModal';

type FormBarcode = {
    barcode: string;
    type: BarcodeType;
};

type ProductFormValues = {
    barcodes: FormBarcode[];
    categoryId: string;
    subcategoryId: string | null;
    externalProductId: string;
    name: string;
    brand: string;
    volume: string;
    description: string;
    comment?: string;
    searchKeywords?: string;
};

function getOdooLink(odooProductId: number) {
    return `https://odoo.ilunch.fr/web#id=${odooProductId}&cids=1&menu_id=141&action=276&model=product.template&view_type=form`;
}

export const GeneralInfoSection = ({
    product,
    categories,
}: {
    product: NonNullable<GetDetailedProductQuery['detailedProduct']> | DefaultProductType;
    categories: GetActiveCategoriesQuery['activeCategories'];
}) => {
    const {
        externalProductId,
        nayaxProductId,
        odooProduct,
        access,
        brand,
        description,
        comment,
        imageUrl,
        isBeingTested,
        isPaused,
        isToBeArchived,
        name,
        nbRatings,
        newUntil,
        packagingImageUrl,
        rating,
        state,
        volume,
        searchKeywords,
    } = product;

    const { register, control, setValue } = useFormContext<ProductFormValues>();
    const {
        fields: barcodeFields,
        append,
        remove,
    } = useFieldArray<ProductFormValues>({
        name: 'barcodes',
        control,
    });

    const watchedName = useWatch<ProductFormValues, 'name'>({ control, name: 'name', defaultValue: name });
    const watchedBrand = useWatch<ProductFormValues, 'brand'>({ control, name: 'brand', defaultValue: brand });
    const watchedVolume = useWatch<ProductFormValues, 'volume'>({ control, name: 'volume', defaultValue: volume });
    const watchedBarcodes = useWatch<ProductFormValues, 'barcodes'>({ control, name: 'barcodes' });

    const categoryOptions = categories.map(({ _id, title }: { _id: string; title: string }) => ({
        label: title,
        value: _id,
    }));

    const initialCategoryId = 'createdAt' in product ? product.category._id : product.categoryId;
    const watchedCategoryId = useWatch<ProductFormValues, 'categoryId'>({
        control,
        name: 'categoryId',
        defaultValue: initialCategoryId,
    });
    const prevCategoryIdRef = useRef(initialCategoryId);

    useEffect(() => {
        if (prevCategoryIdRef.current !== watchedCategoryId) {
            setValue('subcategoryId', null, { shouldDirty: true });
            prevCategoryIdRef.current = watchedCategoryId;
        }
    }, [watchedCategoryId, setValue]);

    const [selectedQRCode, setSelectedQRCode] = useState<number | null>(null);

    async function fileValidationFunction(file: FileWithPreview) {
        return new Promise<FileError[]>((resolve, reject) => {
            const image = new Image();
            image.onload = function () {
                const widthErrors =
                    image.width >= 1200 && image.height >= 1200
                        ? []
                        : [
                              {
                                  message: `L'image doit avoir une dimension de au moins 1200x1200px, votre image fait ${image.width}x${image.height}px`,
                                  code: 'file-invalid-dimension',
                              },
                          ];
                const ratioErrors =
                    (image.width / image.height).toFixed(2) === (1).toFixed(2)
                        ? []
                        : [
                              {
                                  message: `L'image doit avoir un ratio de 1/1, votre image a un ratio de ${(
                                      image.width / image.height
                                  ).toFixed(2)}`,
                                  code: 'file-invalid-dimension',
                              },
                          ];
                const fileErrors = [...widthErrors, ...ratioErrors];
                resolve(fileErrors);
            };
            image.onerror = reject;
            image.src = file.preview;
        });
    }

    const fullName = getProductFullName({
        name: watchedName || name,
        brand: watchedBrand || brand,
        volume: watchedVolume || volume,
    });

    const barcodeOptions = [
        { value: BarcodeType.Product, label: 'Produit' },
        { value: BarcodeType.Packaging, label: 'Colis' },
    ];

    return (
        <SectionContainer title="Informations générales" isInitiallyOpen>
            <ColumnsSectionContainer numberOfColumns={3}>
                <SectionColumn>
                    {'_id' in product ? (
                        <Row>
                            <CopyValue value={product._id} />
                        </Row>
                    ) : null}
                    {nayaxProductId ? (
                        <Row>
                            <CopyValue label="ID Nayax" value={nayaxProductId} />
                        </Row>
                    ) : null}
                    {odooProduct ? (
                        <Row>
                            <ExternalResourceLink
                                href={getOdooLink(odooProduct.externalOdooProductId)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <TotemImage
                                    src="https://www.odoo.com/web/image/res.company/1/logo?unique=1"
                                    alt="Logo Odoo"
                                    size={30}
                                />
                            </ExternalResourceLink>
                            <CopyValue value={odooProduct.externalOdooProductId.toString()} />
                        </Row>
                    ) : null}
                    <DetailFormValue
                        label="Référence"
                        defaultValue={externalProductId}
                        {...register('externalProductId', { required: false })}
                        width="200px"
                        data-test="product-externalProductId"
                    />
                    <Row>
                        <DetailFormImage
                            name="image"
                            label="Image"
                            sublabel="Image du produit (webapp + mobile)"
                            defaultValue={imageUrl}
                            fileValidationFunction={fileValidationFunction}
                            size={175}
                        />
                        <DetailFormImage
                            name="packagingImage"
                            label="Image de l'emballage"
                            sublabel="Miniature de l'image du produit dans le frigo (mobile)"
                            defaultValue={packagingImageUrl}
                            size={175}
                        />
                    </Row>
                    <DetailValue label="Note" value={<Rating rating={rating} numberOfRatings={nbRatings} />} />
                    <DetailList
                        label="Codes-barres"
                        onAdd={() => append({ barcode: '', type: BarcodeType.Product })}
                        items={barcodeFields.map((field, index) => (
                            <React.Fragment key={field.id}>
                                <DetailFormValue
                                    placeholder="Code barre"
                                    {...register(`barcodes.${index}.barcode` as const, { required: true })}
                                />
                                <DetailFormSelect
                                    placeholder="Type de code barre"
                                    defaultValue={field.type}
                                    name={`barcodes.${index}.type`}
                                    options={barcodeOptions}
                                    required
                                    width="30%"
                                />
                                <DetailListRemoveButton onClick={() => remove(index)} />
                                <QRCodeContainer>
                                    <QRCodeButton type="button" onClick={() => setSelectedQRCode(index)}>
                                        <QRCodeCanvas value={watchedBarcodes?.[index]?.barcode || ''} size={30} />
                                    </QRCodeButton>
                                    <QRCodeModal
                                        isOpen={selectedQRCode === index}
                                        onClose={() => setSelectedQRCode(null)}
                                        value={watchedBarcodes?.[index]?.barcode || ''}
                                    />
                                </QRCodeContainer>
                            </React.Fragment>
                        ))}
                    />
                </SectionColumn>
                <SectionColumn>
                    <DetailFormSelect<string>
                        label="Catégorie"
                        placeholder="Sélectionner une catégorie"
                        name="categoryId"
                        defaultValue={initialCategoryId}
                        options={categoryOptions}
                        dataTest="product-category"
                        required
                    />
                    {watchedCategoryId ? <SubcategorySelector categoryId={watchedCategoryId} /> : null}
                    <DetailFormValue
                        label="Nom"
                        defaultValue={name}
                        {...register('name', { required: true })}
                        width="100%"
                        data-test="product-name"
                    />
                    <DetailFormValue
                        label="Marque"
                        defaultValue={brand}
                        {...register('brand', { required: true })}
                        data-test="product-brand"
                    />
                    <DetailFormValue
                        label="Contenance"
                        sublabel="(poids avec emballage, en g ou mL, affiché aux clients)"
                        defaultValue={volume}
                        {...register('volume', { required: true })}
                        data-test="product-volume"
                    />
                    <DetailDisplayValue label="Nom complet" sublabel="(calculé)" value={fullName} />
                </SectionColumn>
                <SectionColumn>
                    <DetailFormTextArea
                        label="Description"
                        defaultValue={description}
                        {...register('description', { required: true })}
                        width="100%"
                        height="130px"
                        data-test="product-description"
                    />
                    {'isOutOfStock' in product ? (
                        <ModificationInfo>
                            <StatusTag
                                state={state}
                                showHeading
                                sublabel="Modifiable dans la section 'Gestion des statuts'"
                            />
                            <InfoTags
                                access={access}
                                isOutOfStock={product.isOutOfStock}
                                isToBeArchived={isToBeArchived}
                                isBeingTested={isBeingTested}
                                newUntil={newUntil ?? null}
                                isPaused={isPaused}
                                isInShortage={product.supplierShortageInfo.inShortage}
                            />
                        </ModificationInfo>
                    ) : null}
                    {'createdAt' in product ? (
                        <Row>
                            <DetailValue
                                label="Dernière mise à jour"
                                value={getExtendedFormattedDatetime(product.updatedAt)}
                            />
                            <DetailValue
                                label="Date de création"
                                value={getExtendedFormattedDatetime(product.createdAt)}
                            />
                        </Row>
                    ) : null}
                    <DetailFormTextArea
                        label="Commentaires"
                        defaultValue={comment}
                        sublabel="Utilisé uniquement en interne pour prendre des notes/ faire des remarques. Pour noter le(s) client(s) pour lequel(s) ce produit est commandé, veuillez utiliser 'Commandé par x (nom d'entreprise)'"
                        {...register('comment')}
                        width="100%"
                        height="130px"
                    />
                    <DetailFormTextArea
                        label="Mots clés de recherche"
                        defaultValue={searchKeywords?.join('\n')}
                        sublabel="Utilisés pour la recherche de produits dans l'application. Séparés par un retour à la ligne."
                        {...register('searchKeywords')}
                        width="100%"
                        height="130px"
                    />
                </SectionColumn>
            </ColumnsSectionContainer>
        </SectionContainer>
    );
};

const QRCodeContainer = styled.div`
    background-color: white;
    height: 30px;
    width: 30px;
    margin: 0 auto;
    position: relative;
`;

const QRCodeButton = styled.button`
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;

    &:hover {
        opacity: 0.8;
    }
`;

const Row = styled.div`
    display: flex;
    align-items: center;

    & > :not(:first-child) {
        margin-left: 15px;
    }
`;

const ExternalResourceLink = styled.a`
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.ctaPrimaryColor};
    text-decoration: none;
    cursor: pointer;
    margin-right: 0;

    &:hover {
        text-decoration: underline;
    }
`;

const ModificationInfo = styled.div`
    display: flex;
    flex-direction: column;

    & > :not(:first-child) {
        margin-top: 15px;
    }
`;
